// What each company costs, on VERA's server (/v1/costs). A company's owners see their own month and switch resources on
// or off; founders see every company and set the fee, the company's own Azure groups and its outside costs.

import { cpFetch } from "./controlPlane";
import { currentLang, dateLocale } from "./i18n";

export type CostLine = { id: string; name: string; amount: number; quantity?: number | null; unit?: string | null; rate?: number | null; estimate: boolean; detail?: string | null };
export type CostSwitch = { id: string; name: string; what: string; on: boolean };
export type CostStatement = {
  company: string; month: string; currency: string;
  resourceGroups: string[]; azure: CostLine[]; azureNote?: string | null;
  engine: CostLine[]; outside: CostLine[]; fee: CostLine;
  switches: CostSwitch[];
  passThrough: number; total: number; at: string;
};
export type OutsideCost = { name: string; monthly: number; note?: string | null };
export type CostPlan = { monthlyFee: number; resourceGroups: string[]; outside: OutsideCost[]; switches: Record<string, boolean> };
export type AllCosts = { month: string; companies: CostStatement[]; unassigned: CostLine[]; azureNote?: string | null; currency: string };

type Token = () => Promise<string | null>;
const q = (month?: string) => (month ? `?month=${encodeURIComponent(month)}` : "");

export const costs = {
  mine: (t: Token, month?: string) => cpFetch<{ statement: CostStatement; canChange: boolean }>(t, `/v1/costs${q(month)}`),
  setSwitch: (t: Token, id: string, on: boolean) => cpFetch<{ switches: Record<string, boolean> }>(t, "/v1/costs/switches", { method: "POST", body: JSON.stringify({ id, on }) }),
  all: (t: Token, month?: string) => cpFetch<AllCosts>(t, `/v1/admin/costs${q(month)}`),
  setTerms: (t: Token, company: string, terms: { monthlyFee: number; resourceGroups: string[]; outside: OutsideCost[] }) =>
    cpFetch<CostPlan>(t, `/v1/admin/costs/${encodeURIComponent(company)}`, { method: "PUT", body: JSON.stringify(terms) }),
  setSwitchFor: (t: Token, company: string, id: string, on: boolean) =>
    cpFetch<{ switches: Record<string, boolean> }>(t, `/v1/admin/costs/${encodeURIComponent(company)}/switches`, { method: "POST", body: JSON.stringify({ id, on }) }),
};

/** "2026-10" for this month, and the month before it. */
export function recentMonths(now = new Date()): string[] {
  const m = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  return [m(now), m(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 15)))];
}

export const monthName = (month: string) => new Date(`${month}-15T12:00:00`).toLocaleDateString(dateLocale(currentLang()), { month: "long", year: "numeric" });

export const money = (n: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Plain names for companies on the founders' screen; anything else shows its id. */
export const COMPANY_NAMES: Record<string, string> = { marine: "Marine (Carolyn)", dama: "DAMA Security", edutask: "EduTask", veraneural: "VERANeural (our own use)" };
export const companyName = (id: string) => COMPANY_NAMES[id] ?? (id.startsWith("marine-") ? `Marine partner · ${id.slice(7)}` : id);

/** "Name | 99 | note" lines, as founders type outside costs. */
export function parseOutside(text: string): OutsideCost[] {
  return text.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[0])
    .map(([name, amount, note]) => ({ name, monthly: Number((amount ?? "0").replace(/[^0-9.]/g, "")) || 0, note: note || null }));
}
export const outsideText = (lines: CostLine[]) => lines.map((l) => [l.name, l.amount, l.detail ?? ""].join(" | ").replace(/ \| $/, "")).join("\n");

/** Azure's service names, said plainly. Anything else shows as Azure names it. */
const SERVICES: Record<string, string> = {
  AzureAppService: "Web apps (App Service)", AzureDatabaseforPostgreSQL: "Database (PostgreSQL)", AzureContainerApps: "Servers (Container Apps)",
  ContainerRegistry: "App images (Container Registry)", Storage: "File storage", KeyVault: "Secrets (Key Vault)", FoundryModels: "AI models",
  FoundryTools: "AI tools (speech, vision)", AzureCognitiveSearch: "Search", AzureMaps: "Maps and weather", PhoneNumbers: "Phone numbers",
  Email: "Email sending", MSBingServices: "Web search (Bing)", LogAnalytics: "Logs", AppConfiguration: "App settings", IoTHub: "Devices (IoT Hub)", Bandwidth: "Data transfer",
};
export const serviceName = (s: string) => SERVICES[s] ?? s;
